import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Save, X } from 'lucide-react';

import { DetailField } from '@/components/common/DetailField';
import { SectionHeading } from '@/components/common/DialogPrimitives';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fmt } from '@/lib/format';
import { categoriesService, inventoryService } from '@/services/inventory';
import { itemAttributeTemplateService } from '@/services/platform/item-attribute-template';
import type {
  DimensionUnit,
  InventoryItem,
  ItemCatalogStatus,
  TaxPreference,
  WeightUnit,
} from '@/types/inventory';
import { ItemUnitsSection } from './ItemUnitsSection';
import { CategoryAttributesField } from './CategoryAttributesField';

const UOM_OPTIONS = [
  'PCS', 'NOS', 'EA', 'SET', 'PRS', 'DOZ', 'UNT',
  'KGS', 'GMS', 'TON', 'LTR', 'MLT', 'MTR', 'CMS',
  'SQM', 'SQF', 'BOX', 'BAG', 'CTN', 'PAC', 'ROL', 'HR', 'DAY', 'OTH',
];
const STATUS_OPTIONS: ItemCatalogStatus[] = ['ACTIVE', 'DISCONTINUED', 'ARCHIVED'];
const TAX_PREFS: { value: TaxPreference; label: string }[] = [
  { value: 'TAXABLE', label: 'Taxable' },
  { value: 'NON_TAXABLE', label: 'Non-taxable' },
  { value: 'EXEMPT', label: 'Exempt' },
  { value: 'OUT_OF_SCOPE', label: 'Out of scope' },
];

interface CatalogForm {
  name: string;
  sku: string;
  itemType: 'GOODS' | 'SERVICE';
  unitOfMeasure: string;
  categoryId: string | undefined;
  brand: string;
  manufacturer: string;
  hsnCode: string;
  reorderPoint: number | undefined;
  status: ItemCatalogStatus;
  tracksBatches: boolean;
  description: string;
  sellingPrice: number | undefined;
  mrp: number | undefined;
  costPrice: number | undefined;
  taxPreference: TaxPreference;
  intraStateTaxRate: number | undefined;
  interStateTaxRate: number | undefined;
  dimensionLength: number | undefined;
  dimensionWidth: number | undefined;
  dimensionHeight: number | undefined;
  dimensionUnit: DimensionUnit;
  weight: number | undefined;
  weightUnit: WeightUnit;
  attributes: Record<string, string | string[]>;
}

function toForm(item: InventoryItem): CatalogForm {
  return {
    name: item.name,
    sku: item.sku,
    itemType: item.itemType,
    unitOfMeasure: item.unitOfMeasure,
    categoryId: item.categoryId ?? undefined,
    brand: item.brand ?? '',
    manufacturer: item.manufacturer ?? '',
    hsnCode: item.hsnCode ?? '',
    reorderPoint: item.reorderPoint ?? undefined,
    status: item.status,
    tracksBatches: item.tracksBatches ?? false,
    description: item.description ?? '',
    sellingPrice: item.sellingPrice ?? undefined,
    mrp: item.mrp ?? undefined,
    costPrice: item.costPrice ?? undefined,
    taxPreference: item.taxPreference,
    intraStateTaxRate: item.intraStateTaxRate ?? undefined,
    interStateTaxRate: item.interStateTaxRate ?? undefined,
    dimensionLength: item.dimensionLength ?? undefined,
    dimensionWidth: item.dimensionWidth ?? undefined,
    dimensionHeight: item.dimensionHeight ?? undefined,
    dimensionUnit: item.dimensionUnit,
    weight: item.weight ?? undefined,
    weightUnit: item.weightUnit,
    attributes: item.attributes ?? {},
  };
}

const num = (s: string): number | undefined => (s ? Number(s) : undefined);

/**
 * Item catalog (the Details tab) — one surface for both view and edit.
 * Read-only by default; the Edit button flips the SAME fields to
 * inputs, so there is no layout gap between viewing and editing. The
 * standalone form is used only for creating a new item.
 */
export function ItemCatalogTab({
  item,
  editing,
  onEditingChange,
}: {
  item: InventoryItem;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CatalogForm>(() => toForm(item));

  const categoriesQuery = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
    staleTime: 60_000,
  });
  const categories = categoriesQuery.data ?? [];

  // Tenant-wide item attribute template — layered on top of the
  // category template. Same staleTime as categories; mutates rarely.
  const tenantTemplateQuery = useQuery({
    queryKey: ['tenancy', 'item-attribute-template'],
    queryFn: () => itemAttributeTemplateService.get(),
    staleTime: 60_000,
  });
  const tenantAttrs = tenantTemplateQuery.data?.itemAttributes ?? [];
  const category = useMemo(
    () => categories.find((c) => c.id === item.categoryId),
    [categories, item.categoryId],
  );
  // While editing, the attribute template must follow the *form's*
  // category (operator may change category mid-edit) — not the
  // persisted item's. Outside edit mode we stay with the saved one.
  const formCategory = useMemo(
    () => categories.find((c) => c.id === form.categoryId),
    [categories, form.categoryId],
  );

  // Re-seed the draft whenever edit mode opens or the item changes, so
  // a cancelled edit never leaks stale values into the next session.
  useEffect(() => {
    if (editing) setForm(toForm(item));
  }, [editing, item]);

  const set = <K extends keyof CatalogForm>(key: K, value: CatalogForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const [hsnRateHint, setHsnRateHint] = useState<string | null>(null);

  // On HSN blur, look the code up in the platform HSN→GST master and
  // auto-fill the tax rate. Suggestion only — operator can override.
  const applyHsnRate = async (hsn: string) => {
    const code = hsn.trim();
    if (!code) {
      setHsnRateHint(null);
      return;
    }
    try {
      const match = await inventoryService.lookupHsnRate(code);
      if (match) {
        setForm((f) => ({
          ...f,
          intraStateTaxRate: match.gstRate,
          interStateTaxRate: match.gstRate,
        }));
        setHsnRateHint(
          `GST ${match.gstRate}% suggested from HSN ${match.matchedCode} — ${match.description}`,
        );
      } else {
        setHsnRateHint(null);
      }
    } catch {
      // Best-effort — a lookup failure leaves the rate as-is.
    }
  };

  const save = useMutation({
    mutationFn: () =>
      inventoryService.update(item.id, {
        name: form.name.trim(),
        sku: form.sku.trim().toUpperCase(),
        itemType: form.itemType,
        unitOfMeasure: form.unitOfMeasure,
        categoryId: form.categoryId || undefined,
        brand: form.brand.trim() || undefined,
        manufacturer: form.manufacturer.trim() || undefined,
        hsnCode: form.hsnCode.trim() || undefined,
        reorderPoint: form.reorderPoint,
        status: form.status,
        tracksBatches: form.tracksBatches,
        description: form.description.trim() || undefined,
        sellingPrice: form.sellingPrice,
        mrp: form.mrp,
        costPrice: form.costPrice,
        taxPreference: form.taxPreference,
        intraStateTaxRate: form.intraStateTaxRate,
        interStateTaxRate: form.interStateTaxRate,
        dimensionLength: form.dimensionLength,
        dimensionWidth: form.dimensionWidth,
        dimensionHeight: form.dimensionHeight,
        dimensionUnit: form.dimensionUnit,
        weight: form.weight,
        weightUnit: form.weightUnit,
        attributes: form.attributes,
      }),
    onSuccess: () => {
      toast.success('Item updated');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onEditingChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  const valid = form.name.trim().length > 0 && form.sku.trim().length > 0;
  const isGoods = form.itemType === 'GOODS';
  const skuRenamed = editing && form.sku.trim().toUpperCase() !== item.sku.toUpperCase();
  const uomOptions = UOM_OPTIONS.includes(item.unitOfMeasure)
    ? UOM_OPTIONS
    : [item.unitOfMeasure, ...UOM_OPTIONS];

  return (
    <div className="space-y-6">
      {/* View / edit toggle */}
      <div className="flex items-center justify-between">
        <SectionHeading title="Catalog" />
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEditingChange(false)}
              disabled={save.isPending}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={!valid || save.isPending}
            >
              <Save className="h-3.5 w-3.5" />
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onEditingChange(true)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        )}
      </div>

      {/* ── Catalog ─────────────────────────────────────────────── */}
      {editing ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="SKU">
            <Input
              value={form.sku}
              onChange={(e) => set('sku', e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label="Type">
            <Select
              value={form.itemType}
              onValueChange={(v) => set('itemType', v as 'GOODS' | 'SERVICE')}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="GOODS">Goods</SelectItem>
                <SelectItem value="SERVICE">Service</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Unit of measure">
            <Select
              value={form.unitOfMeasure}
              onValueChange={(v) => set('unitOfMeasure', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {uomOptions.map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select
              value={form.categoryId ?? '__none'}
              onValueChange={(v) => set('categoryId', v === '__none' ? undefined : v)}
            >
              <SelectTrigger><SelectValue placeholder="Uncategorised" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Uncategorised</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Brand">
            <Input value={form.brand} onChange={(e) => set('brand', e.target.value)} />
          </Field>
          <Field label="Manufacturer">
            <Input
              value={form.manufacturer}
              onChange={(e) => set('manufacturer', e.target.value)}
            />
          </Field>
          <Field label="HSN code">
            <Input
              value={form.hsnCode}
              onChange={(e) => set('hsnCode', e.target.value)}
              onBlur={(e) => applyHsnRate(e.target.value)}
            />
            {hsnRateHint && (
              <p className="mt-1 text-[12px] text-success">{hsnRateHint}</p>
            )}
          </Field>
          <Field label="Reorder point">
            <Input
              type="number"
              inputMode="decimal"
              value={form.reorderPoint ?? ''}
              onChange={(e) => set('reorderPoint', num(e.target.value))}
            />
          </Field>
          <Field label="Catalog status">
            <Select
              value={form.status}
              onValueChange={(v) => set('status', v as ItemCatalogStatus)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s[0] + s.slice(1).toLowerCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Batch tracking">
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 text-[12.5px]">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={form.tracksBatches}
                onChange={(e) => set('tracksBatches', e.target.checked)}
              />
              <span>Track per batch / lot</span>
            </label>
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <DetailField label="Name" value={item.name} />
          <DetailField label="SKU" value={<span className="font-mono">{item.sku}</span>} />
          <DetailField label="Type" value={item.itemType === 'SERVICE' ? 'Service' : 'Goods'} />
          <DetailField label="Unit of measure" value={item.unitOfMeasure} />
          <DetailField label="Category" value={category?.name ?? 'Uncategorised'} />
          <DetailField label="Brand" value={item.brand ?? '—'} />
          <DetailField label="Manufacturer" value={item.manufacturer ?? '—'} />
          <DetailField label="HSN code" value={item.hsnCode ?? '—'} />
          <DetailField
            label="Reorder point"
            value={item.reorderPoint != null ? fmt.number(item.reorderPoint) : '—'}
          />
          <DetailField
            label="Catalog status"
            value={<StatusBadge status={(item.status ?? 'active').toLowerCase()} />}
          />
          <DetailField label="Batch tracking" value={item.tracksBatches ? 'Enabled' : 'Off'} />
        </div>
      )}

      {/* Category-driven attributes — same component for view & edit.
          In editing mode operators can change values; in read-only the
          field renders its disabled controls so existing values stay
          visible and discoverable from the same spot they're edited. */}
      {editing ? (
        <CategoryAttributesField
          category={formCategory}
          tenantAttrs={tenantAttrs}
          value={form.attributes}
          onChange={(next) => set('attributes', next)}
          onCreateOption={(attrName, created) => {
            // The new VALUE lives on whichever scope owns the attr.
            // If the key is in the category template, extend the
            // categories cache; if in the tenant template, extend the
            // tenant template cache. Both writes are optimistic —
            // the BE auto-learns to the right scope on item save.
            const inCategory = formCategory?.attributeTemplate?.itemAttributes?.some(
              (a) => a.name === attrName,
            );
            if (inCategory) {
              qc.setQueryData<typeof categories>(
                ['inventory', 'categories'],
                (prev) => {
                  if (!prev) return prev;
                  return prev.map((c) => {
                    if (c.id !== form.categoryId) return c;
                    const tpl = c.attributeTemplate ?? {
                      itemAttributes: [],
                      variantAxes: [],
                    };
                    const list = (tpl.itemAttributes ?? []).map((a) => {
                      if (a.name !== attrName) return a;
                      const opts = a.options ?? [];
                      if (opts.some((o) => o.toLowerCase() === created.toLowerCase())) {
                        return a;
                      }
                      return { ...a, options: [...opts, created] };
                    });
                    return {
                      ...c,
                      attributeTemplate: { ...tpl, itemAttributes: list },
                    };
                  });
                },
              );
            } else {
              qc.setQueryData<typeof tenantTemplateQuery.data>(
                ['tenancy', 'item-attribute-template'],
                (prev) => {
                  const list = prev?.itemAttributes ?? [];
                  return {
                    itemAttributes: list.map((a) => {
                      if (a.name !== attrName) return a;
                      const opts = a.options ?? [];
                      if (opts.some((o) => o.toLowerCase() === created.toLowerCase())) {
                        return a;
                      }
                      return { ...a, options: [...opts, created] };
                    }),
                  };
                },
              );
            }
          }}
          onCreateAttribute={(attr) => {
            // Brand-new key → goes to the tenant scope by default
            // (since it's not tied to any category). Optimistic
            // cache update so it renders as a regular picker for
            // the rest of this session.
            qc.setQueryData<typeof tenantTemplateQuery.data>(
              ['tenancy', 'item-attribute-template'],
              (prev) => {
                const list = prev?.itemAttributes ?? [];
                if (list.some((a) => a.name.toLowerCase() === attr.name.toLowerCase())) {
                  return prev;
                }
                return {
                  itemAttributes: [
                    ...list,
                    {
                      name: attr.name,
                      options: attr.options,
                      multi: attr.multi,
                      required: false,
                    },
                  ],
                };
              },
            );
          }}
        />
      ) : (
        <AttributesReadView
          category={category}
          tenantAttrs={tenantAttrs}
          value={item.attributes ?? null}
        />
      )}

      {skuRenamed && (
        <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-[11.5px] text-warning-foreground">
          <strong>Heads up:</strong> saving renames the SKU{' '}
          <span className="font-mono">{item.sku}</span> →{' '}
          <span className="font-mono">{form.sku.trim().toUpperCase()}</span>
          {item.variantCount ? ` and ${item.variantCount} child variant(s)` : ''}. Stock,
          orders and barcodes that reference the old SKU update externally.
        </div>
      )}

      <Separator />

      {/* ── Description ─────────────────────────────────────────── */}
      <section className="space-y-2">
        <SectionHeading title="Description" />
        {editing ? (
          <Textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            placeholder="Short product description (visible to operators)."
          />
        ) : (
          <p className="text-[13px] leading-relaxed text-foreground">
            {item.description || '—'}
          </p>
        )}
      </section>

      <Separator />

      {/* ── Pricing ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading title="Pricing" />
        {editing ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Selling price">
              <Input
                type="number"
                inputMode="decimal"
                value={form.sellingPrice ?? ''}
                onChange={(e) => set('sellingPrice', num(e.target.value))}
                className="font-mono tabular-nums"
              />
            </Field>
            <Field label="MRP (incl. tax)">
              <Input
                type="number"
                inputMode="decimal"
                value={form.mrp ?? ''}
                onChange={(e) => set('mrp', num(e.target.value))}
                className="font-mono tabular-nums"
              />
            </Field>
            <Field label="Cost price">
              <Input
                type="number"
                inputMode="decimal"
                value={form.costPrice ?? ''}
                onChange={(e) => set('costPrice', num(e.target.value))}
                className="font-mono tabular-nums"
              />
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailField
              label="Selling price"
              value={item.sellingPrice != null ? fmt.inr(item.sellingPrice) : '—'}
            />
            <DetailField
              label="MRP (incl. tax)"
              value={item.mrp != null ? fmt.inr(item.mrp) : '—'}
            />
            <DetailField
              label="Cost price"
              value={item.costPrice != null ? fmt.inr(item.costPrice) : '—'}
            />
            {item.sellingPrice != null && item.costPrice != null && (
              <DetailField
                label="Margin"
                value={
                  <span
                    className={
                      item.sellingPrice >= item.costPrice
                        ? 'text-success'
                        : 'text-destructive'
                    }
                  >
                    {fmt.inr(item.sellingPrice - item.costPrice)}
                  </span>
                }
              />
            )}
          </div>
        )}
      </section>

      <Separator />

      {/* ── Units of measure (sub-resource, its own editor) ─────── */}
      <section className="space-y-3">
        <SectionHeading
          title="Units of measure"
          hint="Receive in cases, sell in pieces — alternate UoMs convert to base"
        />
        <ItemUnitsSection item={item} />
      </section>

      <Separator />

      {/* ── Tax ─────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading title="Tax (India GST)" />
        {editing ? (
          <div className="grid grid-cols-3 gap-4">
            <Field label="Preference">
              <Select
                value={form.taxPreference}
                onValueChange={(v) => set('taxPreference', v as TaxPreference)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TAX_PREFS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {form.taxPreference === 'TAXABLE' && (
              <>
                <Field label="Intra-state %">
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={form.intraStateTaxRate ?? ''}
                    onChange={(e) => set('intraStateTaxRate', num(e.target.value))}
                  />
                </Field>
                <Field label="Inter-state (IGST) %">
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={form.interStateTaxRate ?? ''}
                    onChange={(e) => set('interStateTaxRate', num(e.target.value))}
                  />
                </Field>
              </>
            )}
          </div>
        ) : (
          <TaxView item={item} />
        )}
      </section>

      {/* ── Fulfilment ──────────────────────────────────────────── */}
      {isGoods && (
        <>
          <Separator />
          <section className="space-y-3">
            <SectionHeading
              title="Fulfilment"
              hint="Used for shipping cost & dim weight"
            />
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <Field label="Length">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={form.dimensionLength ?? ''}
                      onChange={(e) => set('dimensionLength', num(e.target.value))}
                    />
                  </Field>
                  <Field label="Width">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={form.dimensionWidth ?? ''}
                      onChange={(e) => set('dimensionWidth', num(e.target.value))}
                    />
                  </Field>
                  <Field label="Height">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={form.dimensionHeight ?? ''}
                      onChange={(e) => set('dimensionHeight', num(e.target.value))}
                    />
                  </Field>
                  <Field label="Unit">
                    <Select
                      value={form.dimensionUnit}
                      onValueChange={(v) => set('dimensionUnit', v as DimensionUnit)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(['CM', 'MM', 'M', 'IN'] as DimensionUnit[]).map((u) => (
                          <SelectItem key={u} value={u}>{u.toLowerCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <Field label="Weight">
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={form.weight ?? ''}
                      onChange={(e) => set('weight', num(e.target.value))}
                    />
                  </Field>
                  <Field label="Weight unit">
                    <Select
                      value={form.weightUnit}
                      onValueChange={(v) => set('weightUnit', v as WeightUnit)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(['KG', 'G', 'LB', 'OZ'] as WeightUnit[]).map((u) => (
                          <SelectItem key={u} value={u}>{u.toLowerCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <DetailField
                  label="Dimensions (L × W × H)"
                  value={
                    item.dimensionLength != null ||
                    item.dimensionWidth != null ||
                    item.dimensionHeight != null
                      ? `${fmt.number(item.dimensionLength ?? 0)} × ${fmt.number(
                          item.dimensionWidth ?? 0,
                        )} × ${fmt.number(item.dimensionHeight ?? 0)} ${item.dimensionUnit.toLowerCase()}`
                      : '—'
                  }
                />
                <DetailField
                  label="Weight"
                  value={
                    item.weight != null
                      ? `${fmt.number(item.weight)} ${item.weightUnit.toLowerCase()}`
                      : '—'
                  }
                />
              </div>
            )}
          </section>
        </>
      )}

      <Separator />

      {/* ── Audit (always read-only) ────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading title="Audit" />
        <div className="grid grid-cols-2 gap-4">
          <DetailField label="Created" value={fmt.dateTime(item.createdAt)} />
          <DetailField label="Updated" value={fmt.relative(item.updatedAt)} />
        </div>
      </section>
    </div>
  );
}

/** Compact label + control pair for the edit grid. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * Read-only twin of CategoryAttributesField — same layout (one card,
 * primary-tint, Tag icon) so flipping in and out of edit mode doesn't
 * shift things around. Missing optional values show as "—" so the
 * operator can still see the full template at a glance.
 */
function AttributesReadView({
  category,
  tenantAttrs = [],
  value,
}: {
  category:
    | {
        name?: string;
        attributeTemplate?: {
          itemAttributes?: Array<{ name: string; required?: boolean; multi?: boolean }>;
        } | null;
      }
    | undefined;
  tenantAttrs?: Array<{ name: string; required?: boolean; multi?: boolean }>;
  value: Record<string, string | string[]> | null;
}) {
  const catAttrs = category?.attributeTemplate?.itemAttributes ?? [];
  const v = value ?? {};
  // Union — category wins on collision (same merge rule as the edit
  // path / BE validateAttributes).
  const merged = (() => {
    const byName = new Map<string, { name: string; required?: boolean; multi?: boolean }>();
    for (const a of tenantAttrs) byName.set(a.name.toLowerCase(), a);
    for (const a of catAttrs) byName.set(a.name.toLowerCase(), a);
    return Array.from(byName.values());
  })();
  const mergedKeys = new Set(merged.map((a) => a.name.toLowerCase()));
  const extras = Object.entries(v).filter(([k]) => !mergedKeys.has(k.toLowerCase()));
  if (merged.length === 0 && extras.length === 0) return null;
  const display = (raw: string | string[] | undefined): string =>
    Array.isArray(raw) ? raw.join(', ') : (raw ?? '');
  return (
    <div className="rounded-md border border-primary/25 bg-primary/[0.03] px-3.5 py-3">
      <div className="mb-2 text-[12.5px] font-semibold text-foreground">
        Product details
      </div>
      <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {merged.map((a) => {
          const shown = display(v[a.name]);
          return (
            <div key={a.name} className="flex justify-between gap-3 text-[12.5px]">
              <span className="text-muted-foreground">
                {a.name}
                {a.required && <span className="ml-0.5 text-destructive">*</span>}
              </span>
              <span className="font-medium text-foreground">
                {shown || <span className="text-muted-foreground">—</span>}
              </span>
            </div>
          );
        })}
        {extras.length > 0 && (
          <>
            <div className="col-span-full mt-1 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
              Custom fields
            </div>
            {extras.map(([k, raw]) => (
              <div key={k} className="flex justify-between gap-3 text-[12.5px]">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium text-foreground">{display(raw)}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Tax view — shows the item's GST rate AND justifies where it comes
 * from: the HSN code, the matched HSN→GST master entry, and whether
 * the item's stored rate agrees with the master. When the rate is
 * missing or stale, an inline button applies the master rate so the
 * fix happens right where the gap is visible.
 */
function TaxView({ item }: { item: InventoryItem }) {
  const qc = useQueryClient();
  const taxable = item.taxPreference === 'TAXABLE';
  const hsn = item.hsnCode?.trim() ?? '';

  const hsnQuery = useQuery({
    queryKey: ['hsn-rate', hsn],
    queryFn: () => inventoryService.lookupHsnRate(hsn),
    enabled: taxable && !!hsn,
  });
  const match = hsnQuery.data ?? null;

  const intra = item.intraStateTaxRate ?? null;
  const inter = item.interStateTaxRate ?? null;
  const hasRate = intra != null;

  const apply = useMutation({
    mutationFn: (rate: number) =>
      inventoryService.update(item.id, {
        intraStateTaxRate: rate,
        interStateTaxRate: rate,
      }),
    onSuccess: () => {
      toast.success('GST rate applied from the HSN master');
      qc.invalidateQueries({ queryKey: ['inventory', 'items'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not apply rate'),
  });

  const prefLabel =
    item.taxPreference?.toLowerCase().replace(/_/g, ' ') ?? '—';

  if (!taxable) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <DetailField label="Preference" value={prefLabel} />
        <div className="col-span-1 text-[12.5px] text-muted-foreground sm:col-span-2">
          No GST applies — this item is {prefLabel}.
        </div>
      </div>
    );
  }

  const rateText = hasRate
    ? intra === inter || inter == null
      ? `${intra}%`
      : `${intra}% intra · ${inter}% IGST`
    : null;

  // Apparel (HSN ch. 61/62/63) GST is value-based — worth flagging.
  const isApparel = !!match && /^6[123]/.test(match.matchedCode);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <DetailField label="Preference" value={prefLabel} />
        <DetailField
          label="GST rate"
          value={
            rateText ? (
              <span className="font-medium text-foreground">{rateText}</span>
            ) : (
              <span className="font-medium text-destructive">Not set</span>
            )
          }
        />
        {match && (
          <DetailField
            label="HSN master rate"
            value={<span className="font-mono">{match.gstRate}%</span>}
          />
        )}
      </div>

      {/* Justification block */}
      <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2.5 text-[12px]">
        <div className="mb-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          How this rate is determined
        </div>

        {!hsn ? (
          <p className="text-muted-foreground">
            No HSN code on this item — a GST rate can't be derived. Add an HSN
            code (the rate then auto-suggests) or set the rate manually.
          </p>
        ) : hsnQuery.isLoading ? (
          <p className="text-muted-foreground">Checking the HSN→GST master…</p>
        ) : !match ? (
          <p className="text-muted-foreground">
            HSN <span className="font-mono text-foreground">{hsn}</span> isn't in
            the GST master, so no rate can be suggested. Set the rate manually,
            or ask the platform team to add this code.
          </p>
        ) : (
          (() => {
            // Apparel (HSN ch. 61/62) GST is value-based since the Sep
            // 2025 reform: 5% at ≤ ₹2,500/piece, else the master rate.
            // The master carries the above-threshold rate, so for a
            // priced sub-₹2,500 apparel item the correct rate is 5%.
            const suggested =
              isApparel && item.sellingPrice != null && item.sellingPrice <= 2500
                ? 5
                : match.gstRate;
            const correct =
              hasRate &&
              intra === suggested &&
              (inter == null || inter === suggested);
            return (
              <div className="space-y-1.5 text-foreground">
                <p>
                  HSN <span className="font-mono">{hsn}</span> → matches{' '}
                  <span className="font-mono">{match.matchedCode}</span> ·{' '}
                  {match.description} → GST master rate{' '}
                  <span className="font-medium">{match.gstRate}%</span>.
                </p>
                {isApparel && (
                  <p className="text-[11.5px] text-muted-foreground">
                    Garment GST is value-based (Sep 2025 reform):{' '}
                    <span className="font-medium">5%</span> at ₹2,500/piece or
                    below, <span className="font-medium">{match.gstRate}%</span>{' '}
                    above.{' '}
                    {item.sellingPrice != null ? (
                      <>
                        This item is priced {fmt.inr(item.sellingPrice)} →{' '}
                        <span className="font-medium text-foreground">
                          {suggested}% applies
                        </span>
                        .
                      </>
                    ) : (
                      'Set a selling price to fix the slab.'
                    )}
                  </p>
                )}
                {!hasRate ? (
                  <p className="flex flex-wrap items-center gap-2 text-destructive">
                    <span>
                      ⚠ No rate is set on this item — invoices would apply 0%
                      GST.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => apply.mutate(suggested)}
                      disabled={apply.isPending}
                    >
                      {apply.isPending ? 'Applying…' : `Apply ${suggested}%`}
                    </Button>
                  </p>
                ) : correct ? (
                  <p className="text-success">
                    ✓ This item's rate ({rateText}) is correct.
                  </p>
                ) : (
                  <p className="flex flex-wrap items-center gap-2 text-warning">
                    <span>
                      This item's rate ({rateText}) differs from the expected{' '}
                      {suggested}%.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => apply.mutate(suggested)}
                      disabled={apply.isPending}
                    >
                      {apply.isPending ? 'Applying…' : `Set to ${suggested}%`}
                    </Button>
                  </p>
                )}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
}
